/**
 * Pinned-announcement selection (#2674, r1 review fixes).
 *
 * Extracted from ClubChatPage so the three concerns — the cross-page server-pin
 * selection (#1 Frank/George), the 24h client-clock cutoff (#2 George), and the
 * derived fast-path scan — are unit-tested in isolation and cannot drift
 * silently inside the page component (#4 George).
 *
 * PARITY NOTE: mobile keeps a structurally-identical port in
 * `mobile/src/lib/chat.ts` (derivePinnedAnnouncement / selectServerPinnedAnnouncement /
 * pickPinnedAnnouncement). The two are DELIBERATE parallel ports, NOT one shared
 * runtime module: mobile cannot import from `frontend/src/lib` (its Metro/jest
 * module graph would drag the web transport + a SECOND React — the same
 * constraint that forced every other chat helper into chat.ts; see
 * docs/canonical/mobile/contract.md and `reference_shared_hooks_not_native_consumable`).
 * The behaviors are pinned to match by MIRRORED unit tests on both sides, and
 * the two `selectServerPinnedAnnouncement`s scan in OPPOSITE directions because
 * the clients hold pages in opposite orders — see that function's comment.
 */
import type { Message as ApiMessage } from './api/messages';

/**
 * #1445/#2674: the pin window. An announcement pins for 24h; there is no
 * `pinned_until` column or expiry job — the pin is derived as
 * `created_at > now - PINNED_ANNOUNCEMENT_WINDOW_SECONDS`. Matches the server
 * lookup's window (`backend/src/routes/rooms.ts` PINNED_ANNOUNCEMENT_WINDOW_SECONDS)
 * and mobile's constant of the same name.
 */
export const PINNED_ANNOUNCEMENT_WINDOW_SECONDS = 86400;

/** Structural subset of MessagesPage (frontend/src/hooks/useRoomMessages.ts). */
interface PinnedAnnouncementPage {
  pinnedAnnouncement?: ApiMessage | null;
}

/**
 * Derived 24h pin (the FAST PATH): the latest `is_announcement` message within
 * the window, scanned over the ALREADY-LOADED messages. The winner is
 * deterministic (created_at DESC, id DESC) and matches the server read order +
 * mobile's `derivePinnedAnnouncement`, so a same-second announcement resolves to
 * the same message on both platforms. Order-independent (full scan) — the caller
 * may pass messages in any order. `nowSeconds` is passed in for render purity /
 * test determinism (ClubChatPage's 60s-ticked `nowSeconds`).
 */
export function derivePinnedAnnouncement(
  messages: ReadonlyArray<ApiMessage>,
  nowSeconds: number,
): ApiMessage | undefined {
  const cutoff = nowSeconds - PINNED_ANNOUNCEMENT_WINDOW_SECONDS;
  let latest: ApiMessage | undefined;
  for (const m of messages) {
    if (m.is_announcement && m.created_at > cutoff) {
      if (
        !latest ||
        m.created_at > latest.created_at ||
        (m.created_at === latest.created_at && m.id > latest.id)
      ) {
        latest = m;
      }
    }
  }
  return latest;
}

/**
 * Resolve the server pin across the infinite-query pages (#2674 r1 — #1).
 *
 * The server pin is room+time scoped, so every page in a CONSISTENT snapshot
 * carries the SAME value; pages disagree only transiently mid-refetch, where the
 * value from the LATEST-fetched page is authoritative. TanStack refetches
 * infinite pages sequentially from raw index 0 (the newest-messages page) to the
 * raw LAST page (the oldest-messages page) — `infiniteQueryBehavior`'s do/while
 * loop — so the raw-LAST page holds the freshest wall-clock response.
 *
 * WEB sees `select`-REVERSED pages (`selectDisplayOrder` reverses fetch order),
 * so `pages[0]` IS that raw-last / freshest page. We therefore scan from the
 * FRONT and take the first DEFINED value.
 *
 * ⚠️ Mobile keeps pages in RAW order and so scans from the BACK — do NOT "align"
 * the two directions; they are opposite BY CONSTRUCTION (see the parity note).
 *
 * The previous `reduce`-to-last-defined selected `pages[last]` = the raw-FIRST
 * (stalest) page, so a mid-refetch new/cleared pin lagged a poll (#1).
 *
 * `null` (server says "no current pin") is a DEFINED value and wins over an
 * older page's stale announcement, so this reports the freshest server OPINION;
 * `undefined` (a pre-#2674 page) is skipped so a mixed cache falls through to
 * the derived fast path. How that opinion is weighed against the derived pin
 * lives in `pickPinnedAnnouncement` (a `null` opinion no longer strictly
 * suppresses — see that comment).
 */
export function selectServerPinnedAnnouncement(
  pages: ReadonlyArray<PinnedAnnouncementPage>,
): ApiMessage | null | undefined {
  for (const page of pages) {
    if (page.pinnedAnnouncement !== undefined) return page.pinnedAnnouncement;
  }
  return undefined;
}

/**
 * Choose the effective pin (#2674): the NEWEST VALID (sub-24h) candidate from
 * EITHER source — the server pin OR the derived pin.
 *
 * WHY newest-of-both, not strict server-null authority (#2674 r2 — George #1):
 * the two sources answer different questions. The server pin sees the WHOLE room
 * and so covers the paging blind spot (an announcement buried >1 page deep that
 * the derived scan over loaded pages can't see). The derived pin reflects LOCAL
 * mutations instantly — a leader's just-posted announcement is optimistically
 * inserted into the message pages (useRoomMessages onMutate) but NOT into the
 * `pinned_announcement` field, which is only refreshed by the next GET. Under the
 * old "server value/`null` is authoritative, only `undefined` falls back" rule, a
 * defined `null` from the last fetch (the common empty-lobby case) SUPPRESSED the
 * just-posted derived pin until the next 30s poll — a regression of the send
 * happy-path. Taking the newest valid candidate fixes that: a derived pin the
 * server response predates is by definition fresher, so it wins; when the server
 * pin is the newest (blind-spot case) it wins. Deletion does NOT rely on `null`
 * authority: the delete mutation optimistically removes the message from every
 * page AND, on any page whose cached `pinnedAnnouncement` IS that message, clears
 * it to `null` in the same update (MessageActionSheet), so NEITHER source keeps a
 * candidate before the settle-invalidate refetch. (Clearing the list alone was
 * NOT enough — the server candidate is weighed independently of the derived scan,
 * so an un-cleared cached pin kept the banner on a deleted row; Frank/Codex r3.)
 *
 * `null`/`undefined` server pins contribute NO candidate (a `null` server opinion
 * means "the server currently has no pin", not "hide the local one"); the derived
 * pin then decides. This preserves the `undefined`→derived fallback for pre-#2674
 * servers as a special case of "no server candidate".
 *
 * The 24h cutoff is applied to BOTH candidates (#2674 r1 — #2): the server pin is
 * a snapshot from the last fetch and does NOT otherwise re-expire on the client
 * clock, and `nowSeconds` ticks (ClubChatPage's 60s interval) so expiry
 * re-evaluates live without a refetch. Ties (same `created_at`) break on higher
 * `id`, matching `derivePinnedAnnouncement` and the server read order.
 */
export function pickPinnedAnnouncement(
  serverPin: ApiMessage | null | undefined,
  derivedPin: ApiMessage | undefined,
  nowSeconds: number,
): ApiMessage | undefined {
  const cutoff = nowSeconds - PINNED_ANNOUNCEMENT_WINDOW_SECONDS;
  let best: ApiMessage | undefined;
  // `serverPin ?? undefined` folds both `null` (server has no pin) and
  // `undefined` (pre-#2674) into "no server candidate".
  for (const candidate of [serverPin ?? undefined, derivedPin]) {
    if (!candidate || candidate.created_at <= cutoff) continue;
    if (
      !best ||
      candidate.created_at > best.created_at ||
      (candidate.created_at === best.created_at && candidate.id > best.id)
    ) {
      best = candidate;
    }
  }
  return best;
}
